import type { components } from "@/api/client";

// openapi-fetch widens annotation tuples in JSON response media types.
type Annotation = Omit<components["schemas"]["SampleBrowseAnnotation"], "bbox"> & { bbox: number[] | null };
export type BrowseSample = Omit<components["schemas"]["SampleBrowseItem"], "annotations"> & { annotations: Annotation[] };
export type BrowsePage = Omit<components["schemas"]["SampleBrowsePage"], "samples"> & { samples: BrowseSample[] };

export type CatalogDataset = components["schemas"]["DatasetListed"];
export type PreviewSource =
  | { kind: "draft"; number: number; revision: number; sampleCount?: number }
  | { kind: "version"; number: number };

/** Each preview belongs to one immutable source. An open draft never changes the published head. */
export function datasetPreviewSources(dataset: Pick<CatalogDataset, "latest_draft" | "head">): PreviewSource[] {
  const sources: PreviewSource[] = [];
  if (dataset.latest_draft) {
    const draft = dataset.latest_draft;
    sources.push({ kind: "draft", number: draft.number, revision: draft.revision, sampleCount: draft.sample_count });
  }
  if (dataset.head !== null) sources.push({ kind: "version", number: dataset.head });
  return sources;
}

export function previewSourceKey(source: PreviewSource): string {
  return source.kind === "draft" ? `draft:${source.number}:${source.revision}` : `version:${source.number}`;
}

export function previewSourceLabel(source: PreviewSource): string {
  return source.kind === "draft" ? `Draft D${source.number} r${source.revision}` : `Published v${source.number}`;
}

export function previewSourceHref(slug: string, name: string, source: PreviewSource): string {
  const base = `/p/${encodeURIComponent(slug)}/datasets/${encodeURIComponent(name)}`;
  return source.kind === "draft" ? `${base}/drafts/${source.number}?rev=${source.revision}` : `${base}/v/v${source.number}`;
}

export type CatalogFilters = { search: string; category: string; group: string; work: string };
export const EMPTY_CATALOG_FILTERS: CatalogFilters = { search: "", category: "all", group: "all", work: "all" };

export function filterCatalog(rows: CatalogDataset[], filters: CatalogFilters): CatalogDataset[] {
  const search = filters.search.trim().toLocaleLowerCase();
  return rows.filter((dataset) =>
    (filters.category === "all" || dataset.category === filters.category) &&
    (filters.group === "all" || dataset.groups.some((group) => group.id === filters.group)) &&
    (filters.work === "all" || (filters.work === "drafts" ? dataset.open_drafts > 0
      : filters.work === "published" ? dataset.head !== null : dataset.head === null)) &&
    `${dataset.display_name} ${dataset.name} ${dataset.description ?? ""}`.toLocaleLowerCase().includes(search)
  );
}

export const CATALOG_PAGE_SIZE = 12;

export function sortCatalog(rows: CatalogDataset[], sorting: { id: string; desc: boolean }[]): CatalogDataset[] {
  const value = (dataset: CatalogDataset, field: string) => {
    switch (field) {
      case "created": return dataset.created_at;
      case "recent": { const published = dataset.head_facts?.published_at; return published && published > dataset.created_at ? published : dataset.created_at; }
      case "category": return dataset.category;
      case "drafts": return dataset.open_drafts;
      case "versions": return dataset.versions;
      case "published": return dataset.head_facts?.published_at ?? null;
      default: return dataset.display_name;
    }
  };
  return [...rows].sort((a, b) => {
    for (const sort of sorting) {
      const av = value(a, sort.id), bv = value(b, sort.id);
      if (av === null || bv === null) {
        if (av !== bv) return av === null ? 1 : -1;
        continue;
      }
      const difference = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      if (difference) return sort.desc ? -difference : difference;
    }
    return a.name.localeCompare(b.name);
  });
}

/** Clamp a page after refetch or filtering so removing records cannot strand the user on an empty page. */
export function catalogPage<T>(rows: T[], requested: number) {
  const pages = Math.max(1, Math.ceil(rows.length / CATALOG_PAGE_SIZE));
  const page = Math.max(0, Math.min(requested, pages - 1));
  return { page, pages, rows: rows.slice(page * CATALOG_PAGE_SIZE, (page + 1) * CATALOG_PAGE_SIZE) };
}
